import React, { useEffect, useState } from "react";
import { io } from "socket.io-client";
import { Link } from "react-router-dom";
import {
  Chip,
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  FormControl,
  IconButton,
  Badge,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Skeleton,
} from "@mui/material";

import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import CountdownTimer from "../../components/UI/CountdownTimer";
import { getAuctions } from "../../api/auctions";
import { useTheme } from "@mui/material/styles";
import BidIcon from "@mui/icons-material/Gavel";
import AccountIcon from "@mui/icons-material/AccountCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import WoodsIcon from "@mui/icons-material/Forest";
import PropertyIcon from "@mui/icons-material/Landscape";
import RulerIcon from "@mui/icons-material/Straighten";

import MapPreview from "../../components/UI/MapPreview";

const AllAuctions = () => {
  const theme = useTheme();

  const { t } = useTranslation();

  // State grouping for clarity
  const [socket, setSocket] = useState(null);
  const [auctions, setAuctions] = useState([]);

  const [regionFilter, setRegionFilter] = useState("");
  const [propertyTypeFilter, setPropertyTypeFilter] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // const uniquePropertyTypes = ["property", "felling", "timber", "wood_chips"];
  const uniquePropertyTypes = ["property", "felling"];

  // Fetch auctions helper
  const fetchAuctions = async () => {
    setLoading(true); // Ensure loading true at start of fetch
    try {
      const fetchedAuctions = await getAuctions();
      const activeAuctions = fetchedAuctions.filter(
        (a) => a.status === "active"
      );
      setAuctions(activeAuctions);
      setError(null); // Clear any previous error on success
    } catch (err) {
      setError(t("errors.err"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initialize socket connection
    const socketInstance = io(process.env.REACT_APP_API_URL, {
      withCredentials: true,
    });
    setSocket(socketInstance);

    socketInstance.on("connect", () => {});

    socketInstance.on("new_bid", (data) => {
      setAuctions((prev) =>
        prev.map((auction) => {
          if (
            auction.auction_id == data.auctionId &&
            data.bidAmount > (auction.highest_bid || 0)
          ) {
            return { ...auction, highest_bid: data.bidAmount };
          }
          return auction;
        })
      );
    });

    // These events all trigger a fetch to update auctions
    // TODO make the new auctions animate or something
    const refreshEvents = [
      "auction_published",
      "auction_ended",
      "auction_updated",
    ];
    refreshEvents.forEach((event) => socketInstance.on(event, fetchAuctions));

    fetchAuctions();

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  // Unique regions for filter dropdown
  const uniqueRegions = [...new Set(auctions.map((a) => a.region))];

  // Apply filtering and sorting
  const filteredAndSortedAuctions = auctions
    .filter(
      (a) =>
        (!regionFilter || a.region === regionFilter) &&
        (!propertyTypeFilter || a.property_type === propertyTypeFilter)
    )
    .sort((a, b) => {
      const bidA = a.highest_bid ?? a.starting_price;
      const bidB = b.highest_bid ?? b.starting_price;

      switch (sortOption) {
        case "price-asc":
          return bidA - bidB;
        case "price-desc":
          return bidB - bidA;
        case "start-soonest":
          return new Date(a.start_time) - new Date(b.start_time);
        case "end-soonest":
          return new Date(a.end_time) - new Date(b.end_time);
        default:
          return 0;
      }
    });

  return (
    <>
      <Helmet>
        <title>{t("seo.title.all_auctions")}</title>
        <meta property="og:title" content={t("seo.title.all_auctions")} />

        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Box sx={{ mt: { xs: 0, md: 4 }, mb: 4, mx: "auto", maxWidth: "90vw" }}>
        <Typography variant="h2" align="center" sx={{ pb: 2, pt: 4 }}>
          {t("all_auctions.page_title")}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2, px: { xs: 0, md: 2 } }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel>{t("all_auctions.filter_by_region")}</InputLabel>
              <Select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                label={t("all_auctions.filter_by_region")}
              >
                <MenuItem value="">{t("all_auctions.all_regions")}</MenuItem>
                {uniqueRegions.map((region, i) => (
                  <MenuItem key={i} value={region}>
                    {region.charAt(0).toUpperCase() + region.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel>
                {t("all_auctions.filter_by_property_type")}
              </InputLabel>
              <Select
                value={propertyTypeFilter}
                onChange={(e) => setPropertyTypeFilter(e.target.value)}
                label={t("all_auctions.filter_by_property_type")}
              >
                <MenuItem value="">
                  {t("all_auctions.all_property_types")}
                </MenuItem>
                {uniquePropertyTypes.map((type, i) => (
                  <MenuItem key={i} value={type}>
                    {t(`property_types.${type}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel>{t("all_auctions.sort_by")}</InputLabel>
              <Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                label="Kārtot pēc"
              >
                <MenuItem value="">{t("all_auctions.default")}</MenuItem>
                <MenuItem value="price-asc">
                  {t("all_auctions.price_asc")}
                </MenuItem>
                <MenuItem value="price-desc">
                  {t("all_auctions.price_desc")}
                </MenuItem>
                <MenuItem value="end-soonest">
                  {t("all_auctions.end_time")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ padding: { xs: 0, md: 2 } }}>
          {loading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card>
                    <Skeleton variant="rectangular" height={140} />
                    <CardContent>
                      <Skeleton width="60%" />
                      <Skeleton width="80%" />
                      <Skeleton width="40%" />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : filteredAndSortedAuctions.length > 0 ? (
            <Grid container spacing={2}>
              {filteredAndSortedAuctions.map((auction) => {
                const parsedAttachments = JSON.parse(auction.attachments);
                const thumbnailFilename =
                  parsedAttachments[Number(auction.thumbnail)];
                const imageUrl = `${process.env.REACT_APP_API_URL}/${thumbnailFilename}`;

                return (
                  <Grid item xs={12} sm={6} md={3} key={auction.auction_id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transform: "translateY(0px)",
                        transition: "all 0.25s ease-in-out",
                        "&:hover": {
                          boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.03)",
                          transform: "translateY(-4px)",
                        },
                      }}
                    >
                      <Box
                        component={Link}
                        to={`/izsole/${auction.auction_id}`}
                        sx={{
                          height: 160,
                          position: "relative",
                          display: "block",
                        }}
                      >
                        {auction.property_name ? (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              backgroundColor: "primary.main",
                              color: "white",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "4px",
                              fontSize: "0.75rem",
                              fontWeight: "bold",
                              zIndex: 1000,
                            }}
                          >
                            {auction.property_name}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              backgroundColor: "primary.main",
                              color: "white",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "4px",
                              fontSize: "0.75rem",
                              fontWeight: "bold",
                              zIndex: 1000,
                            }}
                          >
                            {t("general.auction_nr")} {auction.auction_id}
                          </Box>
                        )}
                        {Number(auction.thumbnail) >= 0 ? (
                          <CardMedia
                            component="img"
                            height="160"
                            image={imageUrl}
                            alt="Property thumbnail"
                          />
                        ) : (
                          <Box sx={{ height: "100%" }}>
                            <MapPreview
                              lat={JSON.parse(auction.coordinates).lat}
                              lng={JSON.parse(auction.coordinates).lng}
                            />
                          </Box>
                        )}
                      </Box>

                      <CardContent sx={{ flexGrow: 1 }}>
                        {/* Type Chip */}
                        {auction.property_type == "property" && (
                          <Chip
                            sx={{ px: 1, py: 1.5, mb: 1 }}
                            size="small"
                            icon={<PropertyIcon />}
                            label={t("general.property")}
                            variant="outlined"
                          />
                        )}
                        {auction.property_type == "felling" && (
                          <Chip
                            sx={{ px: 1, py: 1.5, mb: 1 }}
                            size="small"
                            icon={<WoodsIcon />}
                            label={t("general.felling")}
                            variant="outlined"
                          />
                        )}
                        {auction.property_type == "timber" && (
                          <Chip
                            sx={{ px: 1, py: 1.5, mb: 1 }}
                            size="small"
                            icon={<WoodsIcon />}
                            label={t("general.timber")}
                            variant="outlined"
                          />
                        )}
                        {auction.property_type == "wood_chips" && (
                          <Chip
                            sx={{ px: 1, py: 1.5, mb: 1 }}
                            size="small"
                            icon={<WoodsIcon />}
                            label={t("general.wood_chips")}
                            variant="outlined"
                          />
                        )}

                        {/* Size Chip */}
                        <Chip
                          sx={{ px: 1, py: 1.5, mb: 1, ml: 1 }}
                          size="small"
                          icon={<RulerIcon />}
                          label={
                            Number(
                              (auction.property_size &&
                                Number(auction.property_size) > 0) ||
                                auction.harvestable_area
                            ).toLocaleString() + " ha"
                          }
                          variant="outlined"
                        />

                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ fontWeight: "bold" }}
                        >
                          €
                          {Number(
                            auction.highest_bid || auction.starting_price
                          ).toLocaleString()}
                          {/* <span style={{ padding: "0px 5px" }}>•</span>
                                                {Number((auction.property_size && Number(auction.property_size) > 0) || auction.harvestable_area).toLocaleString()} ha */}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {auction.property_address ||
                            `ID ${auction.property_id}, ${auction.region}`}
                        </Typography>
                      </CardContent>

                      <Divider />

                      <Grid container>
                        <IconButton
                          disableRipple
                          disableFocusRipple
                          sx={{
                            pointerEvents: "none",
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            badgeContent={
                              <VerifiedIcon
                                color="success"
                                sx={{
                                  width: 16,
                                  height: 16,
                                  backgroundColor: "white",
                                  borderRadius: "50%",
                                }}
                              />
                            }
                          >
                            <AccountIcon
                              color="primary"
                              sx={{ fontSize: 40 }}
                            />
                          </Badge>
                        </IconButton>
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          height="100%"
                        >
                          <Typography
                            sx={{ lineHeight: "1em", fontWeight: "bold" }}
                            variant="body"
                            color="primary"
                          >
                            {auction.business_name ? (
                              auction.business_name
                            ) : (
                              <>
                                {auction.first_name} {auction.last_name}
                              </>
                            )}
                          </Typography>
                          <Typography
                            sx={{ mt: 0, pt: 0, lineHeight: "1em" }}
                            variant="subtitle2"
                            color="primary"
                          >
                            {auction.property_email
                              ? auction.property_email
                              : auction.user_email}
                          </Typography>
                        </Box>
                      </Grid>

                      <Divider />

                      <CardActions
                        sx={{
                          justifyContent: "space-between",
                          px: 2,
                          pb: 2,
                          pt: 2,
                        }}
                      >
                        <CountdownTimer small endTime={auction.end_time} />
                        <Button
                          component={Link}
                          to={`/izsole/${auction.auction_id}`}
                          // variant="outlined"
                          size="small"
                          startIcon={<BidIcon />}
                          sx={{
                            px: 2,
                            fontWeight: "bold",
                            borderStyle: "solid",
                            borderWidth: "1px",
                            borderColor: "theme.palette.primary.main",
                            "&:hover": {
                              boxShadow: "0px 0px 0px 1px rgb(1, 47, 77)",
                            },
                          }}
                          // color="secondary"
                          // fullWidth
                        >
                          {t("all_auctions.card_cta")}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography variant="body2" sx={{ p: 2, textAlign: "center" }}>
              {t("all_auctions.no_results")}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default AllAuctions;
